import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { ErrorBanner } from '@/components/ErrorBanner';
import { RequirePermission } from '@/components/RequirePermission';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDate } from '@/i18n/format';
import type { OwnerReadiness, ReadinessStep } from '@/api/types';
import { REASON_KEYS } from './contractLabels';

export type FixAction = 'WAREHOUSE' | 'CONTRACT' | 'PORTAL';

/** The contract the operator should finish (a draft / suspended one), when there is one. */
function contractIdOf(r: OwnerReadiness): string | null {
  const id = r.steps.find((s) => s.key === 'CONTRACT')?.detail['contractId'];
  return typeof id === 'string' ? id : null;
}

/**
 * The client card's onboarding chain: client -> serving warehouse -> contract -> Portal access ->
 * ready to receive requests. The server computes every step and what is missing in one read
 * (GET /api/v1/office/sellers/:id/readiness); this only words it. Each missing step has the button
 * that fixes it right here (open the contract form / the Portal invite) - no wizard.
 */
export function ReadinessPanel({ ownerId, version, onFix }: { ownerId: string; version: number; onFix: (action: FixAction, contractId?: string | null) => void }): JSX.Element | null {
  const { t, locale } = useLanguage();
  const result = useApi(() => endpoints.ownerReadiness(ownerId), [ownerId, version]);

  if (result.status === 'loading') return null;
  if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;
  const r: OwnerReadiness = result.data;
  // A response without the chain (older server) shows nothing rather than a broken card.
  if (!r.steps) return null;

  function detailOf(step: ReadinessStep): string {
    const d = step.detail;
    switch (step.key) {
      case 'WAREHOUSE': {
        const names = Array.isArray(d['names']) ? (d['names'] as string[]) : [];
        return names.length > 0 ? names.join(', ') : t('onboard.detail.warehouseNone');
      }
      case 'CONTRACT': {
        const number = typeof d.number === 'string' ? d.number : null;
        const date = typeof d.date === 'string' ? formatDate(d.date, locale) : null;
        const num = number ? (date ? t('onboard.detail.contractNumber', { number, date }) : t('onboard.detail.contractNumberOnly', { number })) : '';
        if (step.state === 'done') return num;
        const reason = typeof d.reason === 'string' && d.reason in REASON_KEYS ? t(REASON_KEYS[d.reason as keyof typeof REASON_KEYS]) : '';
        return [reason, num].filter(Boolean).join(' · ');
      }
      case 'PORTAL': {
        const active = Number(d.active ?? 0);
        const pending = Number(d.pending ?? 0);
        if (active > 0) return t('onboard.detail.portalActive', { n: active });
        if (pending > 0) return t('onboard.detail.portalPending', { n: pending });
        return t('onboard.detail.portalNone');
      }
      case 'READY':
        return r.ready ? t('onboard.readyYes') : t('onboard.readyNo');
      default:
        return typeof d.name === 'string' ? d.name : '';
    }
  }

  const firstMissing = r.missing.filter((k): k is FixAction => k === 'WAREHOUSE' || k === 'CONTRACT' || k === 'PORTAL');
  const ent = r.warehouseEntitlement;

  return (
    <section className={`card readiness${r.ready ? ' is-ready' : ''}`} aria-labelledby="readiness-title" data-testid="readiness">
      <h2 id="readiness-title" className="card-title">{t('onboard.title')}</h2>
      <ol className="readiness-steps">
        {r.steps.map((s) => (
          <li key={s.key} className={`readiness-step ${s.state}`} data-step={s.key} data-state={s.state}>
            <span className="readiness-mark" aria-hidden="true">{s.state === 'done' ? '✓' : '•'}</span>
            <span className="readiness-body">
              <span className="readiness-name">{t(`onboard.step.${s.key}` as const)}</span>
              <span className="visually-hidden"> — {t(`onboard.state.${s.state}` as const)}</span>
              <span className="readiness-detail">{detailOf(s)}</span>
            </span>
          </li>
        ))}
      </ol>
      {firstMissing.length > 0 && (
        <div className="readiness-missing">
          <h3 className="readiness-missing-title">{t('onboard.missingTitle')}</h3>
          <ul className="readiness-fixes">
            {firstMissing.map((k) => {
              const permission = k === 'PORTAL' ? 'USERS.WRITE' : 'BILLING.WRITE';
              const text = k === 'WAREHOUSE' ? t('onboard.hint.WAREHOUSE') : k === 'PORTAL' ? t('onboard.hint.PORTAL') : r.contractReason ? t(REASON_KEYS[r.contractReason]) : t('onboard.reason.NONE');
              // "Contract" and "warehouse" share one form: the warehouse step fixes it when none is assigned.
              if (k === 'CONTRACT' && r.missing.includes('WAREHOUSE')) return null;
              return (
                <li key={k}>
                  <span>{text}</span>
                  <RequirePermission permission={permission}>
                    <button type="button" className="primary" onClick={() => onFix(k, k === 'CONTRACT' ? contractIdOf(r) : null)}>{t(`onboard.fix.${k}` as const)}</button>
                  </RequirePermission>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <p className="field-hint">
        {ent.limit === null ? t('onboard.entitlement.unlimited') : t('onboard.entitlement.limited', { used: ent.used, limit: ent.limit, remaining: ent.remaining ?? 0 })}
        {' · '}
        {r.channelsRequireContract ? t('onboard.channels.required') : t('onboard.channels.free')}
      </p>
    </section>
  );
}
